import { AxiosResponse } from "axios";
import { ScheduleResponse } from "./ScheduleAPI";
import axios from "utils/axios";
import {
  API_BASE_URL,
  API_SCHEDULE_PLAN,
} from "utils/constants";

export type SchedulePlanType = "INDIVIDUAL" | "TEAM";
export type SchedulePlanStatus =
  | "READY"
  | "RUNNING"
  | "DONE"
  | "CANCELED"
  | "ERROR";
export type SchedulePlanRecurrenceFrequency =
  | "DAILY"
  | "WEEKLY"
  | "FORTNIGHTLY"
  | "MONTHLY";

export interface SchedulePlanContact {
  id: string;
  name: string;
}

export interface SchedulePlanRecurrence {
  frequency: SchedulePlanRecurrenceFrequency;
  interval: number;
  daysOfWeek: string[];
}

export interface SchedulePlanProcessingNotification {
  path: string;
  token: string;
  subType: string;
  ongoingBatchAction: boolean;
}

export interface CreateSchedulePlanRequest {
  type: SchedulePlanType;
  contacts: SchedulePlanContact[];
  responsibleId: string;
  sportCourtId: string;
  timeFromTo: string;
  startDate: string;
  endDate: string;
  recurrence: SchedulePlanRecurrence;
}

export interface SchedulePlanBase {
  id: string;
  type: SchedulePlanType;
  status: SchedulePlanStatus;
  responsibleId: string;
  requestedByUserId: string | null;
  sportCourtId: string;
  timeFromTo: string;
  startDate: string;
  endDate: string;
  expectedOccurrences: number;
  createdOccurrences: number;
  failedOccurrences: number;
  createdAt: string | null;
  updatedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  userMessage: string | null;
  contacts: SchedulePlanContact[];
  recurrence: SchedulePlanRecurrence;
  processingNotification: SchedulePlanProcessingNotification | null;
}

export interface SchedulePlan extends SchedulePlanBase {
  schedules?: ScheduleResponse[];
}

export interface SchedulePlanDetail extends SchedulePlanBase {
  schedules: ScheduleResponse[];
}

export interface SchedulePlanSummaryResponse {
  active: number;
  endingSoon: number;
  error: number;
  endingSoonWindowDays: number;
}

export interface SchedulePlanQueryFilters {
  status?: SchedulePlanStatus | "ALL";
  type?: SchedulePlanType | "ALL";
  responsibleId?: string;
  requestedByUserId?: string;
  sportCourtId?: string;
  from?: string;
  to?: string;
  order?: string;
}

export interface SchedulePlanQueryRequest {
  limit: number;
  query?: SchedulePlanQueryFilters;
  page: number;
}

export interface SchedulePlanQueryResponse {
  totalPages: number;
  totalElements: number;
  entities: SchedulePlan[] | null;
}

const getById = (id: string): Promise<AxiosResponse<SchedulePlanDetail>> =>
  axios.get<SchedulePlanDetail>(
    `${API_BASE_URL}${API_SCHEDULE_PLAN.BASE_URL}/${id}`
  );

const cancel = (id: string): Promise<AxiosResponse<SchedulePlanDetail>> =>
  axios.post<SchedulePlanDetail>(
    `${API_BASE_URL}${API_SCHEDULE_PLAN.BASE_URL}/${id}/cancel`
  );

const create = (
  payload: CreateSchedulePlanRequest
): Promise<AxiosResponse<SchedulePlan>> =>
  axios.post<SchedulePlan>(
    `${API_BASE_URL}${API_SCHEDULE_PLAN.BASE_URL}`,
    payload
  );

const query = (
  payload: SchedulePlanQueryRequest
): Promise<AxiosResponse<SchedulePlanQueryResponse>> =>
  axios.post<SchedulePlanQueryResponse>(
    `${API_BASE_URL}${API_SCHEDULE_PLAN.BASE_URL}${API_SCHEDULE_PLAN.QUERY_URL}`,
    payload
  );

const summary = (): Promise<AxiosResponse<SchedulePlanSummaryResponse>> =>
  axios.get<SchedulePlanSummaryResponse>(
    `${API_BASE_URL}${API_SCHEDULE_PLAN.BASE_URL}${API_SCHEDULE_PLAN.SUMMARY_URL}`
  );

const schedulePlanAPI = {
  getById,
  cancel,
  create,
  query,
  summary,
};

export default schedulePlanAPI;
